import React, { useState } from "react";

/**
 * Mock carrier-onboarding portal — a public test harness for the Cinesis
 * Onboarding extension. Mirrors the Priority1 application: Basic Information,
 * Billing Address, per-coverage Insurances (with date pickers), Additional
 * Files (real <input type=file> for upload-injection testing), plus a few extra
 * widget types (select, checkboxes, masked date). Controlled inputs, so a fill
 * only "counts" if it fires the React onChange — the same path a real portal uses.
 *
 * Route: /mock-carrier-portal (no auth). The live panel at the bottom shows the
 * current form state so you can see exactly what the extension landed.
 */

const FIELD =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900";
const LABEL = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500";

function Field({ label, name, value, onChange, type = "text", placeholder, required }) {
  const id = `f_${name}`;
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={LABEL}>
        {label}
        {required && <span className="ml-1 text-red-500">required</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className={FIELD}
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options }) {
  const id = `f_${name}`;
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={LABEL}>
        {label}
      </label>
      <select id={id} name={name} value={value ?? ""} onChange={onChange} className={FIELD}>
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border-t border-gray-200 px-6 py-5">
      <h2 className="mb-4 text-lg font-bold text-gray-800">{title}</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function Coverage({ title, prefix, values, set }) {
  // A distinct heading per coverage so the extension's section-scoped matching
  // can tell Auto vs Cargo vs General apart.
  return (
    <div className="sm:col-span-2 rounded-md border border-gray-200 p-4">
      <h3 className="mb-3 text-sm font-bold text-gray-700">{title}</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Insurance Carrier" name={`${prefix}_carrier`} value={values[`${prefix}_carrier`]} onChange={set(`${prefix}_carrier`)} placeholder="Progressive" />
        <Field label="Policy Number" name={`${prefix}_policy`} value={values[`${prefix}_policy`]} onChange={set(`${prefix}_policy`)} placeholder="POL-000123" />
        <Field label="Coverage Amount" name={`${prefix}_amount`} value={values[`${prefix}_amount`]} onChange={set(`${prefix}_amount`)} placeholder="1000000" />
        <Field label="Insurance Agent" name={`${prefix}_agent`} value={values[`${prefix}_agent`]} onChange={set(`${prefix}_agent`)} placeholder="Agent name" />
        <Field label="Effective Date" name={`${prefix}_effective`} type="date" value={values[`${prefix}_effective`]} onChange={set(`${prefix}_effective`)} />
        <Field label="Expiration Date" name={`${prefix}_expiration`} type="date" value={values[`${prefix}_expiration`]} onChange={set(`${prefix}_expiration`)} />
      </div>
    </div>
  );
}

export default function MockCarrierPortal() {
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [equipment, setEquipment] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const set = (name) => (e) => {
    setValues((v) => ({ ...v, [name]: e.target.value }));
    setSubmitted(false);
  };
  const setFile = (name) => (e) => {
    const f = e.target.files && e.target.files[0];
    setFiles((prev) => ({ ...prev, [name]: f ? `${f.name} (${f.size} bytes)` : "" }));
  };
  const toggleEquip = (v) => (e) =>
    setEquipment((prev) => (e.target.checked ? [...prev, v] : prev.filter((x) => x !== v)));

  const DOCS = [
    { name: "w9", label: "W-9 Form" },
    { name: "coi", label: "Certificate of Insurance" },
    { name: "mcAuthority", label: "MC Authority" },
    { name: "signedAgreement", label: "Signed Agreement" },
  ];

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="mx-auto max-w-3xl overflow-hidden rounded-lg bg-white shadow">
        <header className="bg-gray-900 px-6 py-6 text-white">
          <h1 className="text-2xl font-bold">Mock Carrier Application</h1>
          <p className="mt-1 text-sm text-gray-300">
            Test harness for the Cinesis Onboarding extension. Fill via the extension, or by hand.
          </p>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
          }}
        >
          <Section title="Basic Information">
            <Field label="Carrier Name" name="carrierName" value={values.carrierName} onChange={set("carrierName")} placeholder="ACME TRUCKING LLC" required />
            <Field label="Email Address" name="email" type="email" value={values.email} onChange={set("email")} placeholder="dispatch@acme.com" required />
            <Field label="MC Number" name="mcNumber" value={values.mcNumber} onChange={set("mcNumber")} placeholder="1730328" required />
            <Field label="DOT Number" name="dotNumber" value={values.dotNumber} onChange={set("dotNumber")} placeholder="4406241" required />
            <Field label="Factoring Company Name" name="factoring" value={values.factoring} onChange={set("factoring")} placeholder="RTS Financial" />
            <Field label="Federal EIN Number" name="ein" value={values.ein} onChange={set("ein")} placeholder="33-4702017" required />
            <SelectField label="Billing Currency" name="billingCurrency" value={values.billingCurrency} onChange={set("billingCurrency")} options={["USD", "CAD", "MXN"]} />
            <Field label="Phone Number" name="phone" value={values.phone} onChange={set("phone")} placeholder="530-526-6963" required />
          </Section>

          <Section title="Billing Address">
            <div className="sm:col-span-2">
              <Field label="Street Address Line One" name="street1" value={values.street1} onChange={set("street1")} placeholder="123 Regency Frst" required />
            </div>
            <div className="sm:col-span-2">
              <Field label="Street Address Line Two" name="street2" value={values.street2} onChange={set("street2")} />
            </div>
            <div className="sm:col-span-2">
              <Field label="City, State and ZIP Code" name="cityStateZip" value={values.cityStateZip} onChange={set("cityStateZip")} placeholder="San Antonio, TX 78249" required />
            </div>
          </Section>

          <Section title="Insurances">
            <Coverage title="Auto Liability" prefix="auto" values={values} set={set} />
            <Coverage title="Cargo" prefix="cargo" values={values} set={set} />
            <Coverage title="General Liability" prefix="general" values={values} set={set} />
          </Section>

          <Section title="Additional Files">
            {DOCS.map((d) => (
              <div key={d.name} className="sm:col-span-2 flex items-center justify-between gap-3 rounded-md border border-gray-200 p-3">
                <label htmlFor={`file_${d.name}`} className="text-sm font-medium text-gray-700">
                  {d.label}
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{files[d.name] || "no file"}</span>
                  <input id={`file_${d.name}`} name={d.name} type="file" onChange={setFile(d.name)} className="text-xs" />
                </div>
              </div>
            ))}
          </Section>

          <Section title="Other Widgets">
            <Field label="Expiration Date (masked text)" name="maskedDate" value={values.maskedDate} onChange={set("maskedDate")} placeholder="MM/DD/YYYY" />
            <SelectField label="Entity Type" name="entityType" value={values.entityType} onChange={set("entityType")} options={["LLC", "Corporation", "Sole Proprietor", "Partnership"]} />
            <div className="sm:col-span-2">
              <span className={LABEL}>Equipment Types</span>
              <div className="flex flex-wrap gap-4">
                {["Van", "Reefer", "Flatbed", "Box Truck"].map((v) => (
                  <label key={v} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input type="checkbox" checked={equipment.includes(v)} onChange={toggleEquip(v)} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
          </Section>

          <div className="flex items-center justify-between px-6 py-5">
            {submitted ? (
              <span className="text-sm font-semibold text-green-600">✓ Submitted (mock)</span>
            ) : (
              <span className="text-xs text-gray-400">Nothing submitted yet</span>
            )}
            <button type="submit" className="rounded-md bg-gray-900 px-5 py-2 text-sm font-semibold text-white">
              Complete Application
            </button>
          </div>
        </form>
      </div>

      {/* Live state — see exactly what the extension filled. */}
      <div className="mx-auto mt-6 max-w-3xl rounded-lg bg-white p-4 shadow">
        <h3 className="mb-2 text-sm font-bold text-gray-700">Live form state (debug)</h3>
        <pre className="max-h-64 overflow-auto rounded bg-gray-900 p-3 text-[11px] leading-relaxed text-green-300">
{JSON.stringify({ values, equipment, files }, null, 2)}
        </pre>
      </div>
    </div>
  );
}
